import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaBook, FaGraduationCap, FaMapMarkerAlt, FaFlask } from "react-icons/fa";
import "./StateSyllabus.css";
import { ENQUIRY_URL } from "../config";

const CBSE = () => {
  const highlights = [
    {
      icon: "🏛️",
      title: "Available Since",
      value: "2001",
      description: "Sri Vani has offered CBSE curriculum since 2001",
    },
    {
      icon: "🏛️",
      title: "Affiliated To",
      value: "CBSE",
      description: "Central Board of Secondary Education",
    },
    {
      icon: "🌐",
      title: "Curriculum",
      value: "National",
      description: "National level curriculum with continuous assessments",
    },
    {
      icon: "🏫",
      title: "Campus",
      value: "Machohalli",
      description: "Available at Machohalli Campus",
    },
  ];

  const curriculumLevels = [
    {
      level: "Classes 11 – 12",
      icon: <FaFlask />,
      streams: [
        {
          name: "PCMB",
          subjects: ["Physics", "Chemistry", "Mathematics", "Biology"],
        },
        {
          name: "PCMCs",
          subjects: ["Physics", "Chemistry", "Mathematics", "Computer Science"],
        },
      ],
    },
    {
      level: "Classes 6 – 10",
      icon: <FaGraduationCap />,
      subjects: [
        "Mathematics",
        "Science",
        "Social Science",
        "English (1st Language)",
        "Kannada / Hindi / Sanskrit (2nd Language)",
        "3rd Language (Classes 6–8)",
      ],
    },
    {
      level: "Classes 1 – 5 & Kindergarten",
      icon: <FaBook />,
      subjects: [
        "Mathematics",
        "Environmental Science",
        "Social Science",
        "English",
        "Kannada",
        "Hindi",
      ],
    },
  ];

  const campus = {
    name: "Machohalli Campus",
    subtitle: "Macholli",
    tagline: "A Premier Centre of Academic Excellence & Innovation.",
    image: "/images/macholli/campus-main.jpg",
    link: "/campuses/macholli",
  };

  return (
    <div className="state-syllabus-page">
      {/* Page Header */}
      <section
        className="ss-page-header"
        style={{ backgroundImage: "url('/images/cbse/cbse-hero.jpg')" }}
      >
        <div className="ss-header-overlay" />
        <div className="container">
          <motion.div
            className="ss-header-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="ss-header-tag">Central Board of Secondary Education</p>
            <h1>CBSE</h1>
            <p className="ss-header-sub">National Level Curriculum</p>
          </motion.div>
        </div>
      </section>

      {/* Quick Highlights */}
      <section className="ss-highlights-section">
        <div className="container">
          <div className="ss-highlights-grid">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="ss-highlight-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="ss-highlight-icon">{item.icon}</div>
                <div className="ss-highlight-value">{item.value}</div>
                <div className="ss-highlight-title">{item.title}</div>
                <div className="ss-highlight-desc">{item.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Emblem + Bullets Section */}
      <section className="ss-emblem-section">
        <div className="container">
          <div className="ss-emblem-row">
            <motion.div
              className="ss-emblem-col"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/images/cbse/cbse-emblem.jpeg"
                alt="CBSE Emblem"
                className="ss-emblem-img"
              />
            </motion.div>
            <motion.div
              className="ss-bullets-col"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <ul className="ss-key-bullets">
                <li>Available at Sri Vani Since 2001</li>
                <li>Affiliated to CBSE Board</li>
                <li>National Level Curriculum</li>
                <li>Diverse Continuous Assessments</li>
                <li>Available at Machohalli Campus</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Description Section */}
      <section className="ss-description-section">
        <div className="container">
          <motion.div
            className="ss-description-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>
              The Central Board of Secondary Education (CBSE) is a national level
              board of education in India for public and private schools, controlled
              and managed by the Government of India.
            </p>
            <p>
              Unlike the traditional quarterly, half-yearly and annual assessments,
              the CBSE system follows a process of continuous assessment throughout
              the year called Formative Assessments and one exam called Summative
              Assessment.
            </p>
            <div className="ss-desc-cta">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Curriculum Structure Section */}
      <section className="ss-subjects-section section">
        <div className="container">
          <motion.div
            className="ss-subjects-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="ss-section-title">CBSE Curriculum Pathway</h2>
            <p className="ss-section-subtitle">
              Structured learning from Kindergarten through Class 12
            </p>
          </motion.div>

          <div className="cbse-curriculum-grid">
            {curriculumLevels.map((level, index) => (
              <motion.div
                key={index}
                className="ss-subject-block"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
              >
                <div className="ss-subject-block-header">
                  {level.icon}
                  <h3>{level.level}</h3>
                </div>

                {level.streams ? (
                  <div className="cbse-streams">
                    {level.streams.map((stream, si) => (
                      <div key={si} className="cbse-stream-block">
                        <p className="cbse-stream-name">{stream.name}</p>
                        <ul className="ss-subject-list">
                          {stream.subjects.map((sub, idx) => (
                            <li key={idx}>{sub}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <ul className="ss-subject-list">
                    {level.subjects.map((sub, idx) => (
                      <li key={idx}>{sub}</li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Section */}
      <section className="ss-campuses-section section">
        <div className="container">
          <motion.div
            className="ss-campuses-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="ss-section-tag">
              <FaMapMarkerAlt /> Pursue at Machohalli Campus
            </div>
            <h2 className="ss-section-title">Our CBSE Campus</h2>
          </motion.div>

          <div className="cbse-campus-center">
            <motion.div
              className="ss-campus-card cbse-single-campus"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="ss-campus-image">
                <img src={campus.image} alt={campus.name} />
                <div className="ss-campus-overlay">
                  <span>{campus.subtitle}</span>
                </div>
              </div>
              <div className="ss-campus-info">
                <h3>{campus.name}</h3>
                <p>{campus.tagline}</p>
                <Link to={campus.link} className="btn btn-outline ss-explore-btn">
                  Explore Campus
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ss-cta-section">
        <div className="container">
          <motion.div
            className="ss-cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Begin Your CBSE Journey at Sri Vani</h2>
            <p>
              Join a tradition of academic excellence with India's most
              recognized national curriculum.
            </p>
            <div className="ss-cta-buttons">
              <a
                href={ENQUIRY_URL}
                className="btn btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apply for Admission
              </a>
              <Link to="/contact" className="btn btn-outline-white">
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CBSE;
